import {TranslatableResourceType} from 'types';
import {getQueryFromSearch} from 'utilities/url';
import {NotFound} from 'components/NotFound';
import {composeGid} from '@shopify/admin-graphql-api-utilities';
import {toPascalCase} from '@web-utilities/string';
import {useLocalStorage} from '@web-utilities/local-storage';
import {useLocation, useParams} from 'react-router-dom';

import {THEME_ID_STORAGE_KEY} from '../constants';

import {TranslatableResourcePage} from './components';
import {TranslatableResourceProvider} from './context';

export default function TranslatableResource() {
  const {resourceType: resourceTypeFromRoute} = useParams<{
    resourceType: string;
  }>();
  const {search} = useLocation();
  const [{update: updateStoredThemeId}] =
    useLocalStorage<string>(THEME_ID_STORAGE_KEY);

  const query = getQueryFromSearch(search);
  const resourceIdFromURL = query.id;
  const upperCaseResourceType = resourceTypeFromRoute?.toUpperCase();

  if (
    !resourceTypeFromRoute ||
    !upperCaseResourceType ||
    !Object.values<string>(TranslatableResourceType).includes(
      upperCaseResourceType,
    )
  ) {
    return <NotFound />;
  }

  let resourceId = '';
  if (resourceIdFromURL) {
    const themeIdFromURL = query.themeId;
    const firstSettingIdFromURL = query.firstSettingId;
    const appEmbedUuidFromurl = query.appEmbedUuid;

    if (
      [
        TranslatableResourceType.OnlineStoreThemeJsonTemplate.toString(),
        TranslatableResourceType.OnlineStoreThemeSectionGroup.toString(),
      ].includes(upperCaseResourceType) &&
      themeIdFromURL
    ) {
      resourceId = composeGid(
        toPascalCase(resourceTypeFromRoute),
        resourceIdFromURL,
        {[`theme_id`]: themeIdFromURL},
      );
      updateStoredThemeId(themeIdFromURL);
    } else if (
      upperCaseResourceType ===
        TranslatableResourceType.OnlineStoreThemeSettingsCategory.toString() &&
      themeIdFromURL &&
      firstSettingIdFromURL
    ) {
      resourceId = composeGid(
        toPascalCase(resourceTypeFromRoute),
        resourceIdFromURL,
        {
          [`theme_id`]: themeIdFromURL,
          [`first_setting_id`]: firstSettingIdFromURL,
        },
      ).replace(/\s/g, '+');
      updateStoredThemeId(themeIdFromURL);
    } else if (
      upperCaseResourceType ===
        TranslatableResourceType.OnlineStoreThemeAppEmbed.toString() &&
      themeIdFromURL &&
      appEmbedUuidFromurl
    ) {
      resourceId = composeGid(
        toPascalCase(resourceTypeFromRoute),
        resourceIdFromURL,
        {
          [`theme_id`]: themeIdFromURL,
          [`app_embed_uuid`]: appEmbedUuidFromurl,
        },
      );
      updateStoredThemeId(themeIdFromURL);
    } else if (
      [
        TranslatableResourceType.OnlineStoreThemeLocaleContent.toString(),
        TranslatableResourceType.OnlineStoreThemeSettingsDataSections.toString(),
      ].includes(upperCaseResourceType)
    ) {
      resourceId = composeGid(
        toPascalCase(resourceTypeFromRoute),
        parseInt(resourceIdFromURL, 10),
      );
      updateStoredThemeId(resourceIdFromURL);
    } else {
      resourceId = composeGid(
        toPascalCase(resourceTypeFromRoute),
        parseInt(resourceIdFromURL, 10),
      );
    }
  }

  const resourceType = upperCaseResourceType as TranslatableResourceType;

  return (
    <TranslatableResourceProvider
      resourceId={resourceId}
      resourceType={resourceType}
    >
      <TranslatableResourcePage />
    </TranslatableResourceProvider>
  );
}
